import { Injectable } from "@angular/core";
import { ApiService } from "@bca-core/services/api.service";
import { MessageType } from "@bca-shared/models/_enums";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { ImageDialogState } from "@bca-user-data/models/_enums";
import { IImageValidationResponse } from "@bca-user-data/models/image-validation-response";
import { IMiddleNamesResponse } from "@bca-user-data/models/middlenames-reponse";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { UserDataActionTypes } from "@bca-user-data/store/user-data.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { EMPTY, of, zip } from "rxjs";
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";

@Injectable()
export class UserDataEffects {
  fetchMiddleNames = createEffect(() =>
    this._actions.pipe(
      ofType(UserDataActionTypes.FetchMiddleNames),
      withLatestFrom(this._store$.pipe(select(({ userData }) => userData))),
      switchMap(() => {
        this._store$.dispatch(
          this._createAction(
            new userDataActions.MiddleNamesLoading({ loading: true })
          )
        );
        return this._apiService.getMiddleNames().pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.MIDDLENAMES",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message))
            );
            return EMPTY;
          })
        );
      }),
      filter((response: IMiddleNamesResponse) => response.success),
      mergeMap((response: IMiddleNamesResponse) => {
        const { middleNames } = response;
        return this._createActions([
          new userDataActions.SetMiddleNames({ middleNames }),
          new userDataActions.MiddleNamesLoading({ loading: false }),
        ]);
      })
    )
  );

  fetchAddress = createEffect(() =>
    this._actions.pipe(
      ofType(UserDataActionTypes.FetchAddress),
      switchMap((action: userDataActions.FetchAddress) => {
        this._store$.dispatch(
          this._createAction(
            new userDataActions.AddressLoading({ loading: true })
          )
        );
        const searchableAddress = action.payload;
        return zip(
          this._apiService.getAddress(searchableAddress).pipe(
            catchError((error) => {
              const message = {
                body: "NOTIFICATION.ERROR.ADDRESS",
                type: MessageType.ERROR,
                context: { error },
                originalException: error,
              };
              this._store$.dispatch(
                this._createAction(new appActions.SetMessage(message))
              );
              return EMPTY;
            })
          ),
          of(searchableAddress)
        );
      }),
      switchMap(([response, searchableAddress]) => {
        const success = response.length > 0;
        const { straat, plaats } = success
          ? response[0]
          : { straat: null, plaats: null };
        return zip(
          of({
            ...searchableAddress,
            street: success ? straat : null,
            city: success ? plaats : null,
          }),
          of(success)
        );
      }),
      withLatestFrom(
        this._store$.pipe(select(({ userData }) => userData.selectedProduct))
      ),
      mergeMap(([[address, success], { id, user }]) =>
        this._createActions([
          new userDataActions.SetAddressSearchFailed({ failed: !success }),
          new userDataActions.SetAddressManualMode({
            productId: String(id),
            manual: false,
          }),
          new userDataActions.SelectUser({ ...user, address }),
          new userDataActions.AddressLoading({ loading: false }),
        ])
      )
    )
  );

  startImageUpload = createEffect(() =>
    this._actions.pipe(
      ofType(UserDataActionTypes.StartImageUpload),
      switchMap((action: userDataActions.StartImageUpload) => {
        const { payload: image } = action;
        this._store$.dispatch(
          this._createAction(
            new userDataActions.ImageUploading({ loading: true })
          )
        );
        return this._apiService.validateImage(image).pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.IMAGE_UPLOAD",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message))
            );
            return EMPTY;
          })
        );
      }),
      map((response: IImageValidationResponse[]) => [...response].shift()),
      mergeMap((response: IImageValidationResponse) => {
        const {
          compliant,
          faceAsBase64: imageAsBase64,
          failingMetrics,
        } = response;

        const result = [
          this._createAction(
            new userDataActions.SetImageDialogState({
              state: ImageDialogState.RESULT,
            })
          ),
          this._createAction(
            new userDataActions.ImageUploading({ loading: false })
          ),
        ];

        return compliant
          ? [
              ...result,
              this._createAction(
                new userDataActions.SetImage({ imageAsBase64 })
              ),
              this._createAction(new userDataActions.SetImageMetrics(null)),
            ]
          : [
              ...result,
              this._createAction(new userDataActions.SetImage(null)),
              this._createAction(
                new userDataActions.SetImageMetrics(failingMetrics)
              ),
            ];
      })
    )
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, UserDataEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, UserDataEffects);
  }

  constructor(
    private _actions: Actions,
    private _store$: Store<IRootState>,
    private _apiService: ApiService
  ) {}
}
